/**
 * W3C Trace Context INBOUND vocabulary: the `traceparent` field grammar, the pure parser turning one inbound header
 * into a 32-hex trace id, and the per-request env key that carries that id from a worker entry to the flow engine.
 *
 * WHY THE ENV AND NOT THE FLOW INPUT. `input` IS logged and serialized on every run; `env` reaches a step handler as
 * the third argument and is not (`.claude/skills/service-spinup/SKILL.md`). The request bearer already rides that
 * channel under `authBearer` (`tools/flow-engine/create-dispatch-route-handler.ts`), and an inbound trace id is the
 * same class of per-request fact -- so it is staged the same way rather than minting a second convention. The staged
 * env is always a FRESH object: `asEnvRecord` is a cast, so the worker env a runner is built over is the
 * isolate-lifetime object and writing a per-request fact onto it would leak that fact into the next request's run.
 *
 * ONE TRACE-ID VOCABULARY, AND IT IS NOW GENUINELY ONE. The value this parser yields is the same 32-lowercase-hex
 * shape `otlpTraceIdFor` (`const/otlp-span-protocol.ts`) derives from a run id, `FlowRunRecord.traceId` carries,
 * `resolveTelemetryIdentity` (`const/telemetry-identity.ts`) produces on EVERY arm, and `mintTraceIdHex` below mints
 * for the browser's realm-scoped id (`utils/telemetry/page-load-traceparent.ts`) -- so the choke point takes any of
 * them with no conversion. The second vocabulary that used to
 * sit under the same name, a raw UUID correlation id, was retired when that leaf moved every arm onto `otlpTraceIdFor`;
 * its own header records the divergence as history rather than as a live hazard.
 *
 * It is still NOT branded with `TraceId` (`types/primitives-identifiers.ts`), for the reason that outlived the
 * divergence: a trace id is minted as a plain string and the brand is applied at the CONSUMING boundary through
 * `parseTraceId`, which is where a caller-supplied value is admitted. Branding it here would move that admission into a
 * parser whose whole contract is that the value may be garbage.
 *
 * THE FORMAT HALF LANDED 2026-08-09 WITH ITS FIRST PRODUCER, exactly as this docstring previously reserved it. That
 * producer is the BROWSER: `prepareHeaders` (`components/lib/api/fetch-helpers.ts`) stamps a `traceparent` on every
 * request `fetchAdminApi` builds, minting one trace id per page load and a fresh span id per request. Carrying a trace
 * across a hop this fleet does not itself instrument remains a VERBATIM pass-through (`outboundTraceHeaders`) and
 * still needs no composition -- the formatter exists for the ORIGINATING edge, which has no inbound header to relay.
 *
 * The composer lives HERE rather than at that call site for the reason `QUAL-TRACEPARENT-HAND-ROLLED-001`'s `fix`
 * states: a second copy of the grammar omits the spec rejections this leaf's parser makes, so it emits ids a collector
 * drops -- and a trace that silently fails to join is indistinguishable from one nobody propagated. Composer and
 * parser sharing one file is what makes the round trip testable as a property rather than as two hand-kept fixtures.
 */

import { STANDARD_HEADERS } from '@federa/const/http-header-names';
import { trimToEmpty, nonEmptyStringOrUndefined  } from '@federa/const/string-guards';

/** Version `00` -- the only version any producer emits today, and the one version whose field count is EXACTLY four. */
const TRACEPARENT_VERSION_00 = '00';

/** Trace-id width in hex characters, mirrored from the OTLP wire width so the two sources cannot disagree about shape. */
const TRACE_ID_HEX_CHARS = 32;

/** Span-id width in hex characters. */
const SPAN_ID_HEX_CHARS = 16;

/** The all-zero trace id is INVALID per the spec -- a producer emitting it is signalling "no trace", not a trace of zeros. */
const ALL_ZERO_TRACE_ID = '0'.repeat(TRACE_ID_HEX_CHARS);

/** The all-zero PARENT span id is invalid for the same reason, and a header carrying one is malformed as a whole. */
const ALL_ZERO_SPAN_ID = '0'.repeat(SPAN_ID_HEX_CHARS);

/**
 * `version-traceId-parentId-flags`. The version class excludes `ff`, which the spec reserves as INVALID. A trailing
 * group is captured rather than rejected because a FUTURE version (`01`..`fe`) is allowed to append fields and a
 * forward-compatible parser must still read the first three; the version-`00` exactness check is applied separately,
 * since four fields is a `00` requirement rather than a grammar-wide one.
 */
const TRACEPARENT_RE = /^(?!ff)([\da-f]{2})-([\da-f]{32})-([\da-f]{16})-[\da-f]{2}(-.*)?$/;

/**
 * The 32-hex trace id of a well-formed inbound `traceparent`, or `null`.
 *
 * TOTAL AND NEVER THROWING, which is the whole contract at an ingress: the header is caller-supplied, so every
 * malformed shape -- wrong widths, a non-hex byte, a truncated field, the reserved `ff` version, an all-zero id, a
 * `00` header with appended junk, a non-string -- returns `null` and the run falls back to deriving its trace from its
 * own run id. A trace id is telemetry correlation and nothing else, so rejecting a request over a bad header would
 * trade a real user-facing failure for a cosmetic one.
 *
 * Case is normalized DOWN before matching. The spec requires lowercase hex, so an uppercase header is technically
 * invalid -- but the id it names is unambiguous, and dropping the trace over letter case would disconnect a trace for
 * no reader's benefit.
 *
 * @runtime-derived-ssot-helper: the runtime read of the same-file `TRACEPARENT_RE` grammar and its
 * `TRACEPARENT_VERSION_00` / `ALL_ZERO_TRACE_ID` / `ALL_ZERO_SPAN_ID` rejection consts, none of which is exported --
 * relocating the parser would mean exporting this leaf's module-private grammar for a single reader.
 */
export function parseTraceparentTraceId(headerValue: string | null | undefined): string | null {
	return parseTraceparentFields(headerValue)?.traceId ?? null;
}

/** Both ids a well-formed inbound `traceparent` names: the trace it belongs to and the span that SENT it. */
interface TraceparentFields {
	traceId: string;
	/** The sender's span id -- the PARENT of any span this fleet mints for the request. */
	parentSpanId: string;
}

/**
 * The whole-header parse: every rejection the grammar makes, in ONE place.
 *
 * Both public readers delegate here rather than each running the version / all-zero / trailing-bytes checks, because a
 * second copy of those rejections is the shape that admits an id the other reader drops -- and a header parsed as
 * valid-for-trace but invalid-for-parent would emit a span pointing at a parent no collector can resolve. Same
 * total, never-throwing contract as the public reader above: every malformed shape yields `null`.
 *
 * @runtime-derived-ssot-helper: the runtime read of the same-file `TRACEPARENT_RE` grammar and its
 * `TRACEPARENT_VERSION_00` / `ALL_ZERO_TRACE_ID` / `ALL_ZERO_SPAN_ID` rejection consts.
 */
function parseTraceparentFields(headerValue: string | null | undefined): TraceparentFields | null {
	const match = typeof headerValue === 'string' ? TRACEPARENT_RE.exec(headerValue.trim().toLowerCase()) : null;
	if (match === null) return null;
	const [, version, traceId, spanId, trailing] = match;
	// A version-`00` header is EXACTLY four fields; only a later version may append, so trailing bytes here are malformed.
	if (version === TRACEPARENT_VERSION_00 && trailing !== undefined) return null;
	if (traceId === undefined || traceId === ALL_ZERO_TRACE_ID) return null;
	if (spanId === undefined || spanId === ALL_ZERO_SPAN_ID) return null;
	return { traceId, parentSpanId: spanId };
}

/**
 * The per-request env key an entry stages a parsed inbound trace id under, mirroring `authBearer`.
 *
 * TWO READER CLASSES, and only the first answers "which trace is this run on". The LIFT is ONE PLACE PER PUBLIC ENTRY
 * LANE, both putting it on `ExecuteOptions.traceId` for the engine's depth-0 record builder to resolve:
 * `executeFlowWithTracking` (`tools/flow-engine/execute-with-tracking.ts`) for the by-NAME lane, and
 * `toExecuteOptions` (`tools/flow-engine/create-run-flow-def.ts`) for the def-taking `runFlowDef` lane. That is not the
 * "second answer" the one-reader rule forbids: a dispatch takes exactly ONE lane, and both sites read through
 * `inboundTraceIdFromEnv` below, so neither can answer a run differently. A THIRD reader deciding a run's trace by any
 * other route WOULD be. OUTBOUND FORWARDERS are the second class: `outboundTraceFields` below COPIES the
 * id onto a hop this fleet produces (the assistant-queue envelope), and `outboundTraceparentFromEnv`
 * (`const/run-trace-context.ts`, homed there because its higher-precedence arm reads that leaf's own parent-run keys)
 * COMPOSES the header for a service-binding hop -- each deciding nothing about the current run. Reach for a named
 * forwarder rather than reading the key directly, so the two classes stay countable.
 */
export const INBOUND_TRACE_ID_ENV_KEY = 'inboundTraceId';

/**
 * The per-request env key carrying the inbound `traceparent`'s PARENT span id, staged beside the trace id above and by
 * the same writers, because the two facts arrive in one header and are useless apart: a trace id with no parent joins
 * the right trace but hangs the run's root span at the trace ROOT, which renders as a second, parentless top-level
 * span next to the caller's -- the trace joins but its shape is a lie about who called whom.
 *
 * SAME-TRACE INBOUND HOP ONLY. This is the parent of a span in the trace we were already handed, which is exactly the
 * case `parentSpanId` is FOR; the links-not-parents rule governs CROSS-trace
 * association (a consumer resuming work on its own trace), and nothing here touches that.
 */
export const INBOUND_PARENT_SPAN_ID_ENV_KEY = 'inboundParentSpanId';

/*
 * BOTH INBOUND KEYS ARE EXPORTED WITH NO PRODUCTION IMPORTER, deliberately (QUAL-DEAD-EXPORT-003 `test-only export`,
 * adjudicated 2026-08-21), and the reason is the NEGATIVE half of what they govern. Eleven suites across `apps/`,
 * `flows/`, `microservices/`, `spa/`, `tools/` and `flow-engine/` reach `INBOUND_TRACE_ID_ENV_KEY`, and the load-bearing
 * rows are absence claims about a produced record: `expect(Object.keys(env)).not.toContain(KEY)` (a forwarded id must
 * not leak onto a SIBLING message's env), `expect(KEY in buildDoFlowEnv(...)).toBe(false)` (an absent id stages no
 * slot rather than an empty one), `expect(Object.keys(session)).not.toContain(KEY)` (the id must not be persisted into
 * the session record). The exported in-file consumers are the two `*FromEnv` readers, and each answers `undefined`
 * for BOTH "key absent" and "key present, value rejected" -- so re-pointing at one, which is what the dead-export
 * `fix` prescribes, turns every row above into an assertion that cannot fail on the leak it exists to catch. `in` and
 * `Object.keys` need the KEY; no reader supplies it.
 */

/** The minimal header-reading surface this leaf needs, declared structurally so the const floor takes no Worker-runtime global.
 *  EXPORTED because callers were re-spelling the structure inline to type their own `inboundRequest` options -- a
 *  hand-rolled respelling of this SSOT, which is a defect rather than a style choice. */
export interface TraceHeaderCarrier {
	headers: { get(name: string): string | null };
}

/**
 * The env FRAGMENT staging an ALREADY-PARSED trace id -- the seam for a carrier that arrives as a VALUE rather than on
 * a live `Request`.
 *
 * Every Durable-Object dispatch wrapper is that shape: a DO method holds the isolate and its bindings but not the
 * request that provoked it, so the id has to be handed in by whichever frame DID hold the request. Keeping the
 * fragment's construction here rather than at each wrapper is what stops four DO trees hand-spelling
 * `INBOUND_TRACE_ID_ENV_KEY`, which is the shape a reader-side rename silently desyncs.
 *
 * Empty for an absent, non-string or empty id, for the reason `inboundTraceEnv` states below.
 *
 * THE ID-ONLY HALF, and it is a different fact from {@link inboundTraceEnvFromTraceparent} below rather than a second
 * spelling of it: a caller holding a bare trace id holds NO parent span (a DO wrapper was handed the id by the frame
 * that parsed the header, and that frame kept the span for its own run), so staging one would fabricate a reference.
 * A caller holding the `traceparent` FIELD holds both and stages both.
 *
 * @runtime-derived-ssot-helper: the value-carrier writer of the same-file `INBOUND_TRACE_ID_ENV_KEY` -- centralising the key's
 * use here is exactly what stops four DO trees hand-spelling it, as the paragraph above states.
 */
export function inboundTraceEnvFromId(traceId: string | null | undefined): Record<string, string> {
	return typeof traceId === 'string' && traceId !== '' ? { [INBOUND_TRACE_ID_ENV_KEY]: traceId } : {};
}

/**
 * The env FRAGMENT staging one `traceparent` FIELD VALUE -- the seam for a hop that carries the header's bytes as a
 * VALUE rather than on a live `Request`.
 *
 * The out-of-process subflow lane is that shape: the isolate composes a `traceparent` from its own run's staged trace
 * (`outboundTraceparentFromEnv`, `const/run-trace-context.ts`), the value rides the `/cap/subflow` wire, and the cold
 * child stages it on the run env it is about to dispatch on -- so the child's record and spans join the parent's trace
 * instead of rooting a second one. Keeping the parse HERE rather than at that child is what stops the wire lane from
 * re-deriving a grammar {@link inboundTraceEnv} already owns.
 *
 * BOTH keys or NEITHER: they come from one field and one parse, so there is no shape where the id is stageable and the
 * parent is not. Empty for an absent or malformed value, which every caller reads as "this hop carried no trace".
 *
 * @runtime-derived-ssot-helper: the runtime read of the same-file `parseTraceparentFields` grammar onto the same-file
 * `INBOUND_TRACE_ID_ENV_KEY` / `INBOUND_PARENT_SPAN_ID_ENV_KEY` pair.
 */
export function inboundTraceEnvFromTraceparent(headerValue: string | null | undefined): Record<string, string> {
	const fields = parseTraceparentFields(headerValue);
	return fields === null ? {} : { [INBOUND_TRACE_ID_ENV_KEY]: fields.traceId, [INBOUND_PARENT_SPAN_ID_ENV_KEY]: fields.parentSpanId };
}

/**
 * The env FRAGMENT staging an inbound trace id, spread into a per-request env by each entry seam.
 *
 * It returns an EMPTY record when the request carried no parseable `traceparent`, and that emptiness is load-bearing
 * rather than tidy: an entry can test it to decide whether a per-request env copy is needed at all, so an untraced
 * request keeps the exact dispatch path it had before this seam existed.
 *
 * @runtime-derived-ssot-helper: reads the `STANDARD_HEADERS.TRACEPARENT` header-name SSOT and hands the value to the
 * same-file {@link inboundTraceEnvFromTraceparent}, so the request lane and the wire lane stage one fragment shape.
 */
export function inboundTraceEnv(request: TraceHeaderCarrier): Record<string, string> {
	return inboundTraceEnvFromTraceparent(request.headers.get(STANDARD_HEADERS.TRACEPARENT));
}

/** The 32-hex trace id off a live request, or `undefined` -- the value form a DO entry frame hands to a dispatch wrapper.
 *  @runtime-derived-ssot-helper: the same-file parser read over the `STANDARD_HEADERS.TRACEPARENT` header-name SSOT,
 *  differing from `inboundTraceEnv` only in returning the bare value a DO wrapper can carry. */
export function inboundTraceIdOf(request: TraceHeaderCarrier): string | undefined {
	return parseTraceparentTraceId(request.headers.get(STANDARD_HEADERS.TRACEPARENT)) ?? undefined;
}

/** True when the request carries a trace worth staging -- the test an entry uses before paying for a per-request env copy.
 *  @runtime-derived-ssot-helper: the same-file parser read over `STANDARD_HEADERS.TRACEPARENT`, reduced to a boolean so
 *  the emptiness test and the fragment it gates cannot disagree about what counts as a trace. */
export function hasInboundTrace(request: TraceHeaderCarrier): boolean {
	return parseTraceparentTraceId(request.headers.get(STANDARD_HEADERS.TRACEPARENT)) !== null;
}

/** The staged inbound trace id off a run's env, or `undefined` when the run was not entered on an upstream trace.
 *  @runtime-derived-ssot-helper: the READ half of the same-file `INBOUND_TRACE_ID_ENV_KEY`, paired with
 *  `inboundTraceEnvFromId` so a rename of that key cannot desync writer from reader. */
export function inboundTraceIdFromEnv(env: Record<string, unknown> | undefined): string | undefined {
	return nonEmptyStringOrUndefined(env?.[INBOUND_TRACE_ID_ENV_KEY]);
}

/** The staged inbound PARENT span id off a run's env, or `undefined` when the run was not entered across a traced hop.
 *  @runtime-derived-ssot-helper: the READ half of the same-file `INBOUND_PARENT_SPAN_ID_ENV_KEY`, paired with the
 *  `inboundTraceEnv` writer so a rename of that key cannot desync writer from reader. */
export function inboundParentSpanIdFromEnv(env: Record<string, unknown> | undefined): string | undefined {
	return nonEmptyStringOrUndefined(env?.[INBOUND_PARENT_SPAN_ID_ENV_KEY]);
}

/** The trace id as ONE member, only when present: an absent id leaves NO key, so a record that never met a trace stays
 *  byte-identical to one built before the trace seam existed. The trace-id half every trace-bearing record spreads --
 *  the option pair below, the queue envelope's outbound fields and the telemetry identity record.
 *  @runtime-derived-ssot-helper: composed by the same-file {@link traceOptionMembers} and {@link outboundTraceFields}. */
export function traceIdMember(traceId: string | undefined): { traceId?: string } {
	return traceId === undefined ? {} : { traceId };
}

/** A run's trace pair as executor-option members, each only when present: an absent member leaves NO key, so an
 *  untraced run's options bag stays byte-identical to one that never met a trace. KEYED, so the trace id and the parent
 *  span id cannot be handed over in each other's place (`QUAL-MULTI-ID-BARE-STRING-001`).
 *  @runtime-derived-ssot-helper (utils-floor-pinned): `utils/flow-engine/run-trace-staging.ts` value-binds it, and the
 *  same-file {@link inboundTraceOptions} composes it. */
export function traceOptionMembers(pair: { readonly traceId: string | undefined; readonly parentSpanId: string | undefined }): {
	traceId?: string;
	parentSpanId?: string;
} {
	const { traceId, parentSpanId } = pair;
	return { ...traceIdMember(traceId), ...(parentSpanId !== undefined && { parentSpanId }) };
}

/** The INBOUND trace pair a forwarded env carries, as option members. Lifted together because one writer stages both
 *  off one header: lifting one without the other would join the caller's trace but not its shape.
 *  @runtime-derived-ssot-helper: the two same-file READ halves above, composed through {@link traceOptionMembers}. */
export function inboundTraceOptions(env: Record<string, unknown> | undefined): { traceId?: string; parentSpanId?: string } {
	return traceOptionMembers({ traceId: inboundTraceIdFromEnv(env), parentSpanId: inboundParentSpanIdFromEnv(env) });
}

/**
 * The inbound `traceparent` header VERBATIM, as the header record an outbound forwarder merges to continue the trace
 * across a hop it does not itself instrument -- empty when the inbound request carried no valid one.
 *
 * Verbatim rather than recomposed on purpose: a pass-through hop mints no span, so it has no span id to name and no
 * business rewriting the parent link. The header is re-VALIDATED before forwarding (via the parser) so a malformed
 * inbound value is dropped at this fleet's edge instead of being relayed onward.
 *
 * @runtime-derived-ssot-helper: re-validates through the same-file parser and re-emits under the same
 * `STANDARD_HEADERS.TRACEPARENT` name it read, so the forward cannot name a header the read side does not.
 */
export function outboundTraceHeaders(request: TraceHeaderCarrier): Record<string, string> {
	const raw = request.headers.get(STANDARD_HEADERS.TRACEPARENT);
	return parseTraceparentTraceId(raw) === null || raw === null ? {} : { [STANDARD_HEADERS.TRACEPARENT]: raw };
}

/**
 * `outboundTraceHeaders` for an OPTIONAL carrier: the header record when a dispatch is serving an inbound request, an
 * empty record when it is an honest trace ROOT. Every service client that relays a trace asked this same question with
 * its own inline ternary.
 *
 * @runtime-derived-ssot-helper: delegates to `outboundTraceHeaders` above rather than re-reading the header itself.
 */
export function optionalTraceHeaders(request: TraceHeaderCarrier | undefined): Record<string, string> {
	return request ? outboundTraceHeaders(request) : {};
}

/** A BARE trace id, derived from the width const so this shape and the header grammar's cannot disagree. */
const TRACE_ID_RE = new RegExp(`^[\\da-f]{${TRACE_ID_HEX_CHARS}}$`);

/** A BARE span id, derived from the same width const the header grammar uses. Declared HERE, beside its trace-id twin
 * and above the first reader, because the ADMISSION half (`asInboundSpanId`) and the EMISSION half
 * (`formatTraceparent`) share it -- a `const` initialized after its first use is a temporal-dead-zone hazard the
 * `no-use-before-define` rule exists to close, and the sharing is the whole point of a single declaration. */
const SPAN_ID_RE = new RegExp(`^[\\da-f]{${SPAN_ID_HEX_CHARS}}$`);

/**
 * The ONE admission walk both bare-id readers below run: total and never throwing, whitespace-trimmed, case-normalized
 * DOWN, width-checked against the caller's own derived shape, and rejecting the all-zero id the spec reserves for
 * "absent". Module-local rather than exported -- the two public readers ARE the API, and each supplies the width regex
 * and reserved id that make it a span reader or a trace reader.
 *
 * @runtime-derived-ssot-helper (same-file): the shared body of {@link asInboundSpanId} and {@link asInboundTraceId},
 * collected here so the admission POLICY is decided once. Spelled twice, the two could drift on trimming, on case, or
 * on whether the reserved id is rejected -- and a trace id admitted on terms a span id is not is exactly the
 * disagreement the paired width consts exist to prevent.
 */
function asInboundHexId(value: unknown, shape: RegExp, reservedAbsentId: string): string | undefined {
	if (typeof value !== 'string') return undefined;
	const normalized = value.trim().toLowerCase();
	return shape.test(normalized) && normalized !== reservedAbsentId ? normalized : undefined;
}

/**
 * A caller-supplied BARE 16-hex span id, admitted on exactly the terms `asInboundTraceId` admits a trace id: total,
 * never throwing, case-normalized DOWN, and rejecting the all-zero id the spec reserves for "no span".
 *
 * The engine's record choke point admits the staged parent id through this rather than trusting it, for the reason the
 * trace-id resolver states: the entry seam parses the header today, but the choke point must not assume every FUTURE
 * producer of the option does. A malformed seed yields `undefined` and the root span is emitted parentless -- an
 * honest root -- instead of pointing at bytes no collector can resolve.
 *
 * It reads the SAME `SPAN_ID_RE` the formatter half consumes rather than minting a second width regex: the
 * admission shape and the emission shape must not be able to disagree about what a span id is.
 *
 * @runtime-derived-ssot-helper Runtime-deriving reader of the same-file `SPAN_ID_RE` SSOT, paired with the formatter
 * half in this leaf so admission and emission cannot diverge; `types/flow-engine-execution.ts` reaches it from below
 * the utils layer floor.
 */
export function asInboundSpanId(value: unknown): string | undefined {
	return asInboundHexId(value, SPAN_ID_RE, ALL_ZERO_SPAN_ID);
}

/**
 * A caller-supplied BARE trace id -- one carried by a NON-header hop, where there is no `traceparent` grammar to parse
 * because the producer minted no span and so had nothing to compose with `formatTraceparent`. Live carrier:
 * the assistant-queue envelope's `traceId` member (`types/assistant-queue-message.ts`).
 *
 * TOTAL AND NEVER THROWING, for the same reason the header parser is: a queue message is caller-supplied bytes, so a
 * wrong width, a non-hex byte, uppercase, an all-zero id or a non-string yields `undefined` and the consumer run falls
 * back to deriving its trace from its own run id. Losing correlation is the correct price; failing the message is not.
 * Case is normalized DOWN, matching the header parser's reasoning exactly.
 *
 * @runtime-derived-ssot-helper: the runtime read of the same-file `TRACE_ID_RE` and `ALL_ZERO_TRACE_ID`, both derived
 * from `TRACE_ID_HEX_CHARS` -- the one width const that also fixes the header grammar, so the two cannot disagree.
 */
export function asInboundTraceId(value: unknown): string | undefined {
	return asInboundHexId(value, TRACE_ID_RE, ALL_ZERO_TRACE_ID);
}

/**
 * The trace FRAGMENT a producer running INSIDE a flow merges onto an outbound non-HTTP hop it emits -- the queue-hop
 * sibling of `outboundTraceHeaders`, reading the run's staged inbound trace off `env` rather than off a request.
 *
 * It returns an EMPTY record when the producing run was not entered on an upstream trace, and that emptiness is the
 * contract rather than a degraded case: the spread adds no member, the message is byte-identical to the one that hop
 * sent before this seam existed, and the consumer honestly starts a fresh trace. A producer has no span id and no way
 * to reach its own engine-derived trace, so fabricating a value here would assert a link no collector can resolve.
 *
 * The member NAME is the shared trace-id vocabulary (`FlowRunRecord.traceId`, `AssistantQueueMessage.traceId`), not a
 * `traceparent` header: an envelope is not an HTTP hop and has no header to carry.
 *
 * @runtime-derived-ssot-helper: reads the run's staged id through the same-file `inboundTraceIdFromEnv`, so the queue
 * forwarder reaches `INBOUND_TRACE_ID_ENV_KEY` through the named accessor rather than spelling the key again.
 */
export function outboundTraceFields(env: Record<string, unknown> | undefined): { traceId?: string } {
	return traceIdMember(inboundTraceIdFromEnv(env));
}

/* ORIGINATING-EDGE half: minting and composing a `traceparent` where there is no inbound one to relay. */

/** Trace-flags byte meaning SAMPLED. The originating edge is the only frame that can decide sampling, and this fleet samples everything -- emitting `00` (not-sampled) would tell a collector to drop the trace this producer just started. */
const TRACEPARENT_SAMPLED_FLAGS = '01';

/** Two hex chars per byte, for the minters' byte-count arithmetic. */
const HEX_CHARS_PER_BYTE = 2;

/** The minimal Web Crypto surface, declared structurally rather than taken from lib.dom -- mirroring `CryptoLike` in `utils/crypto/uuid.ts`, so this const floor couples to no Worker-runtime or DOM global. */
interface RandomSource {
	getRandomValues: <T extends ArrayBufferView>(array: T) => T;
}

// Module-scoped declare for the Web Crypto global (not in lib.es2022), same shape the uuid leaf uses.
declare const crypto: RandomSource;

/**
 * `byteCount` random bytes as lowercase hex, or `''` when no randomness source exists.
 *
 * NOT a hash and NOT derived from any id: a trace this edge ORIGINATES has nothing to derive from, which is exactly
 * what distinguishes it from `otlpTraceIdFor` (`const/otlp-span-protocol.ts`), whose whole contract is determinism off
 * a run id. The `typeof crypto === 'undefined'` guard is what keeps this total in an environment that has none -- a
 * bare reference to an absent global would throw a ReferenceError out of a header builder.
 */
function randomHex(byteCount: number): string {
	if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') return '';
	const bytes = new Uint8Array(byteCount);
	crypto.getRandomValues(bytes);
	let hex = '';
	for (let index = 0; index < bytes.length; index++) hex += (bytes[index] as number).toString(16).padStart(HEX_CHARS_PER_BYTE, '0');
	return hex;
}

/**
 * A version-`00` `traceparent` field value, or `''` when either id is unusable.
 *
 * BOTH ids are re-validated through this leaf's own vocabulary before composition -- the trace id through
 * `asInboundTraceId` (which also rejects the all-zero id and normalises case), the span id through the width regex and
 * the all-zero check. That is not defensive padding: it is what makes the composer and the parser agree BY
 * CONSTRUCTION, so a value this function emits is one `parseTraceparentTraceId` accepts, and the round trip is a
 * property rather than a pair of hand-kept fixtures.
 *
 * Empty on failure rather than throwing, matching every other total helper in this leaf: a trace id is telemetry
 * correlation and nothing else, so a request must go out un-traced rather than fail over a header.
 *
 * ONE NAMED-MEMBER RECORD, never two positional strings. Both ids are bare lowercase hex differing only in WIDTH, so a
 * positional pair is transposable with no type error -- and the transposition is SILENT rather than loud, because a
 * 16-hex span id fails `asInboundTraceId` and the function returns `''`, which every caller reads as the ordinary
 * "send no header" outcome. The members are what make the swap unspellable (`QUAL-MULTI-ID-BARE-STRING-001`). Branding
 * the two ids is the alternative this leaf refuses, for the reason its module header already gives: it would move
 * caller-value admission into a parser whose whole contract is that the value may be garbage.
 *
 * @runtime-derived-ssot-helper: composes the same-file `TRACEPARENT_VERSION_00` and width consts that fix
 * `TRACEPARENT_RE`, so the emitted field cannot drift from the grammar the parser enforces.
 */
export function formatTraceparent({ traceId, spanId }: { traceId: string; spanId: string }): string {
	const trace = asInboundTraceId(traceId);
	const span = trimToEmpty(spanId).toLowerCase();
	if (trace === undefined || !SPAN_ID_RE.test(span) || span === ALL_ZERO_SPAN_ID) return '';
	return `${TRACEPARENT_VERSION_00}-${trace}-${span}-${TRACEPARENT_SAMPLED_FLAGS}`;
}

/**
 * A fresh random trace id at the width the header grammar fixes, or `''` when no randomness source exists.
 *
 * THE MINTERS ARE EXPORTED AND THE REALM STATE IS NOT (2026-09-08, row 79 of the terminal arm): the per-page-load
 * trace id this leaf used to hold as a module-scope `let` is state with a ground of its own (one trace per realm), and
 * the const floor holds data and pure transforms only (`QUAL-CONST-MODULE-SCOPE-MUTABLE-BINDING-001`). That state now
 * lives in `utils/telemetry/page-load-traceparent.ts`, beside its one consumer's floor, and composes these two
 * minters -- so the WIDTHS stay here, spelled once beside the parser that enforces them.
 *
 * @runtime-derived-ssot-helper: composes the same-file `randomHex`, `TRACE_ID_HEX_CHARS` and `HEX_CHARS_PER_BYTE`, so
 * no caller names a width.
 */
export function mintTraceIdHex(): string {
	return randomHex(TRACE_ID_HEX_CHARS / HEX_CHARS_PER_BYTE);
}

/** A fresh random span id at the width the header grammar fixes, or `''` when no randomness source exists.
 *  @runtime-derived-ssot-helper: composes the same-file `randomHex`, `SPAN_ID_HEX_CHARS` and `HEX_CHARS_PER_BYTE`. */
export function mintSpanIdHex(): string {
	return randomHex(SPAN_ID_HEX_CHARS / HEX_CHARS_PER_BYTE);
}

/**
 * The PARENT-SPAN fields of a run record, ADMITTED not trusted and OMITTED when absent or malformed. There is
 * deliberately no derived fallback: a trace id can be derived from a run id because that run genuinely owns the trace,
 * but a parent span is a claim about a span the run does NOT own -- an upstream process's, or the in-process run that
 * dispatched it -- so a value invented here would fabricate a reference no collector can resolve, and the honest
 * answer is a parentless root. Spread as `...parentSpanFields(...)` so an unseeded record is byte-identical to a
 * record with no parent rather than carrying an explicit `undefined`.
 *
 * @runtime-derived-ssot-helper: the record-shaped read of the same-file `asInboundSpanId`, so no caller re-spells the
 * admit-or-omit decision.
 */
export function parentSpanFields(seed: string | undefined): { parentSpanId?: string } {
	const parentSpanId = asInboundSpanId(seed);
	return parentSpanId === undefined ? {} : { parentSpanId };
}
